/**
 * @author        woody@zumper.com (Woody Anderson)
 * @copyright     Copyright (c) 2017, Zumper
 */

import { getHours, getMinutes, getSeconds, isValid } from 'date-fns'

export type RawHours = readonly [day: string, start: string, end: string | null]

export interface HoursTime {
  readonly hour: number
  readonly minute: number
  readonly seconds: number
}

export type ValidDay = 'sun' | 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat'
export type ValidHour = `${number}${'' | ':'}${number | ''}${'' | ' '}${'a' | 'p' | 'A' | 'P'}${
  | 'm'
  | 'M'}${'' | string}`
export interface HoursByAppointmentOnly {
  start: 'By Appointment Only' | 'by appointment only'
  end: null | 'By Appointment Only' | 'by appointment only'
}
export type ValidHours = Hours & { day: ValidDay } & (
    | { start: ValidHour; end: ValidHour }
    | HoursByAppointmentOnly
  )

const HOUR_REGEX = /(^(1[0-2]|0?[1-9]):?([0-5][0-9])? ?([AaPp][Mm]))/
const validDays = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat']

export class Hours {
  public readonly day?: string
  public readonly start?: string
  public readonly end?: string | null
  public readonly startTime?: HoursTime | null
  public readonly endTime?: HoursTime | null

  constructor(array: RawHours) {
    if (Array.isArray(array)) {
      const [day, start, end] = array
      this.day = day
      this.start = start
      this.end = end
      this.startTime = parseOfficeHourTime(start)
      this.endTime = parseOfficeHourTime(end)
    }
  }

  static isByAppointmentOnly<H extends Hours>(
    hours: H | undefined
  ): hours is H & HoursByAppointmentOnly {
    return (
      !!hours &&
      !!hours.start &&
      hours.start.toLowerCase() === 'by appointment only' &&
      (hours.end == null || hours.end.toLowerCase() === 'by appointment only')
    )
  }

  static isNonEmptyHours(hours: readonly Hours[] | null | undefined): hours is readonly Hours[] {
    return Array.isArray(hours) && !!hours.length
  }

  /**
   * Checks that the given hour string is in HH:MM am/pm format
   *
   * @param hour
   */
  static isValidHour(hour: string | null | undefined): hour is ValidHour {
    return hour != null && HOUR_REGEX.test(hour)
  }

  static isValidHours(hours: Hours | undefined): hours is ValidHours {
    return (
      !!hours &&
      !!hours.day &&
      validDays.includes(hours.day) &&
      !!hours.start &&
      ((!!hours.end && Hours.isValidHour(hours.start) && Hours.isValidHour(hours.end)) ||
        Hours.isByAppointmentOnly(hours))
    )
  }

  static isValidSchedule(
    hours: readonly Hours[] | null | undefined
  ): hours is readonly ValidHours[] {
    if (Hours.isNonEmptyHours(hours)) {
      return hours.every(Hours.isValidHours)
    }
    return false
  }
}

export const parseOfficeHourTime = (hourString: string | null | undefined) => {
  if (!Hours.isValidHour(hourString)) {
    return null
  }
  let validHourString: string = hourString
  const hasColon = hourString.includes(':')
  if (!hasColon) {
    const afterDigitsIndex = validHourString.search(/\s?[AaPp][Mm]/)
    // adding :00 after hour
    validHourString = `${validHourString.slice(0, afterDigitsIndex)}:00${validHourString.slice(
      afterDigitsIndex
    )}`
  }

  const hasSpaceBeforeMeridianIndicator = validHourString.search(/\s[AaPp][Mm]/) > -1
  if (!hasSpaceBeforeMeridianIndicator) {
    const meridianIndicatorIndex = validHourString.search(/[AaPp][Mm]/)
    // adding a space after time and before am/pm
    validHourString = `${validHourString.slice(0, meridianIndicatorIndex)} ${validHourString.slice(
      meridianIndicatorIndex
    )}`
  }
  // this date has no significance, just using it to utilize Date constructor to parse the hour
  // string to an actual time
  const date = new Date(`25 Jun 2020 ${validHourString}`)
  if (isValid(date)) {
    return {
      hour: getHours(date),
      minute: getMinutes(date),
      seconds: getSeconds(date),
    }
  }
  return null
}
